import { fetchAuthSession } from "@aws-amplify/auth";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { EmailDocument } from "../../hooks/useEmailsRestApi";
import { Button } from "../ui/button";

type Message = { sender: "user" | "ai"; text: string };
type ChatBoxProps = {
  email: EmailDocument | null;
  disabled?: boolean;
  disabledMessage?: string;
  onLimitError?: (message: string) => void;
};

// API Gateway 経由の chat-stream エンドポイント
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const CHAT_STREAM_ENDPOINT = `${API_ENDPOINT}/api/emails/chat-stream`;

const ChatBox: React.FC<ChatBoxProps> = ({
  email,
  disabled = false,
  disabledMessage = "現在のプランでは利用できません",
  onLimitError,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // 自動スクロール
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  // Cognitoトークンを取得
  const getAuthToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      // Cognito User Pool Authorizer requires ID Token (not Access Token)
      return session.tokens?.idToken?.toString() || null;
    } catch (_e) {
      return null;
    }
  };

  // SSEストリーミングでチャットメッセージを送信
  const sendChatMessage = async ({
    userMessage,
    emailSubject,
    emailBody,
  }: {
    userMessage: string;
    emailSubject: string;
    emailBody: string;
  }) => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("認証トークンの取得に失敗しました");
    }

    // 既存のリクエストをキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const response = await fetch(CHAT_STREAM_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        emailId: email?.id || "",
        emailSubject,
        emailBody,
        userMessage,
      }),
      signal: abortControllerRef.current.signal,
    });

    if (!response.ok) {
      const err = new Error(
        response.status === 429
          ? "AIヘルパーの利用上限に達しました"
          : `HTTP ${response.status}: ${response.statusText}`,
      );
      (err as Error & { status: number }).status = response.status;
      throw err;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Response body is not readable");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;
        const value = result.value;

        buffer += decoder.decode(value, { stream: true });

        // SSE形式のパース（data: で始まる行を処理）
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // 最後の不完全な行を保持

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6); // "data: " を削除

            if (data === "[DONE]") {
              return; // ストリーム終了
            }

            // エラーチェック
            try {
              const parsed = JSON.parse(data);
              if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch {
              // JSONでない場合はテキストとして処理
            }

            // AIメッセージを更新
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.sender === "ai") {
                return [
                  ...prev.slice(0, -1),
                  { sender: "ai", text: (last.text || "") + data },
                ];
              }
              return [...prev, { sender: "ai", text: data }];
            });
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  // 送信ハンドラ
  const handleSend = async () => {
    if (!input.trim() || !email) return;

    const userMsg = input.trim();
    setMessages(prev => [...prev, { sender: "user", text: userMsg }]);
    setInput("");
    setIsStreaming(true);

    try {
      await sendChatMessage({
        userMessage: userMsg,
        emailSubject: email.subject ?? "",
        emailBody: email.content ?? "",
      });
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        const status = (err as Error & { status?: number }).status;
        if (status === 429 && onLimitError) {
          onLimitError(
            err instanceof Error
              ? err.message
              : "AIヘルパーの利用上限に達しました",
          );
        } else {
          setMessages(prev => [
            ...prev,
            {
              sender: "ai",
              text:
                err instanceof Error
                  ? `エラーが発生しました: ${err.message}`
                  : "エラーが発生しました。",
            },
          ]);
        }
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-[500px] border rounded-lg p-4 bg-gray-50">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto mb-4 space-y-4 relative"
      >
        {disabled ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 text-center px-4 select-none">
            <p className="text-lg">{disabledMessage}</p>
          </div>
        ) : messages.length === 0 && !isStreaming ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 text-center px-4 select-none">
            <p className="text-lg mb-2">案件についてAIに質問してみましょう!</p>
            <p className="text-sm">
              例:「このメールを要約して」「勤務形態は?」「単価はいくらですか?」
            </p>
          </div>
        ) : null}

        {!disabled &&
          messages.map((msg, idx) => (
            <div
              key={`${msg.sender}-${idx}`}
              className={`p-3 rounded-lg max-w-[80%] whitespace-pre-wrap ${
                msg.sender === "user"
                  ? "bg-primary text-white self-end ml-auto"
                  : "bg-white border border-gray-200 text-gray-800 self-start mr-auto shadow-sm"
              }`}
            >
              {msg.text}
            </div>
          ))}

        {!disabled &&
          isStreaming &&
          messages[messages.length - 1]?.sender !== "ai" && (
            <div className="text-gray-500 text-sm">AIが入力中...</div>
          )}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
          placeholder={disabled ? "" : "メッセージを入力..."}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e =>
            e.key === "Enter" && !e.nativeEvent.isComposing && handleSend()
          }
          disabled={disabled || isStreaming}
        />
        <Button
          onClick={handleSend}
          className="px-4 py-2 flex items-center gap-1"
          disabled={disabled || isStreaming || !input.trim()}
        >
          送信
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-4 h-4"
          >
            <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

export default ChatBox;
